import React, { useState } from 'react';
import StageShell from './common/StageShell';
import { getProject, setStage, getSelectedArchitecture } from '../../services/sdlcProject.service';
import { synthesizeDeployment } from './sdlcSynthesizers';

/**
 * SDLC – Deployment / CI-CD stage.
 *
 * Generates IaC + pipeline manifests appropriate for the selected
 * architecture. The user can re-run after switching architectures.
 */
function DeploymentStage() {
  const [project, setProject] = useState(getProject());
  const arch = getSelectedArchitecture();
  const deployment = project?.deployment;
  const [activeFile, setActiveFile] = useState(0);

  const handleGenerate = () => {
    const result = synthesizeDeployment({
      architecture: arch,
      requirementsReport: project?.requirements?.snapshot,
      cicd: project?.cicd,
    });
    const next = setStage('deployment', result);
    setProject(next);
    setActiveFile(0);
  };

  const inputs = [
    { label: 'Architecture', value: arch?.name || '' },
    { label: 'Code', value: project?.code?.snapshot ? `${(project.code.snapshot.files || []).length} files` : '' },
    { label: 'Tests', value: project?.tests?.framework || '' },
    { label: 'CI/CD', value: project?.cicd?.provider || '' },
  ];

  const files = deployment?.files || [];

  return (
    <StageShell
      stageId="deployment"
      description="Generate infrastructure-as-code and CI/CD pipeline files for the chosen architecture."
      inputs={inputs}
      canContinue={files.length > 0}
      actions={<button className="stage-btn primary" onClick={handleGenerate} disabled={!arch || !project?.cicd?.files?.length}>
        {deployment ? 'Regenerate' : 'Generate deployment'}
      </button>}
    >
      {!arch && (
        <div className="stage-empty">
          <div className="icon">🏛️</div>
          No architecture selected. Pick one in the Architecture stage first.
        </div>
      )}

      {arch && deployment && (
        <div style={{ marginBottom: 10, fontSize: '0.9rem', color: '#374151' }}>
          Provider: <strong>{deployment.provider}</strong>
        </div>
      )}

      {arch && files.length === 0 && (
        <div className="stage-empty">
          <div className="icon">🚀</div>
          {project?.cicd?.files?.length
            ? <>Click <em>Generate deployment</em> to produce AWS deployment files for <strong>{arch.name}</strong>.</>
            : <>Generate and approve CI/CD before preparing deployment.</>}
        </div>
      )}

      {files.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, marginTop: 12 }}>
          <div className="stage-file-list" style={{ marginTop: 0 }}>
            {files.map((f, i) => (
              <button
                key={f.path}
                type="button"
                className="stage-file-item"
                style={{
                  textAlign: 'left',
                  cursor: 'pointer',
                  background: activeFile === i ? '#eff6ff' : 'transparent',
                }}
                onClick={() => setActiveFile(i)}
              >
                <span>{f.path}</span>
              </button>
            ))}
          </div>
          <pre className="stage-pre">{files[activeFile]?.content}</pre>
        </div>
      )}
    </StageShell>
  );
}

export default DeploymentStage;
